"use client";

import AlphabetPattern from "@/components/section-infographic/abc-page/Alphabetpattern";
import FAQ from "@/components/ui/FAQ";
import { use, useEffect, useState } from "react";
import { detailsBySlug, getItemBySlug, type DetailSection, type ContentBlock } from "../data";

type PageProps = {
  params: Promise<{ slug: string }>;
};

export default function AtoZDetailPage({ params }: PageProps) {
  const resolvedParams = use(params);
  const slug = resolvedParams.slug;

  /* ================= Case Validation ================= */

  useEffect(() => {
    const checkCaseMismatch = async () => {
      try {
        const validateResponse = await fetch(
          `/api/paths/validate?path=${encodeURIComponent(
            `/a-to-z-of-branding/${slug}`
          )}`,
          { cache: "no-store" }
        );

        if (validateResponse.ok) {
          const validateData = await validateResponse.json();
          if (
            validateData.exists &&
            validateData.needsRedirect &&
            validateData.correctPath
          ) {
            window.location.replace(
              validateData.correctPath +
                window.location.search +
                window.location.hash
            );
          }
        }
      } catch (err) {
        console.error("Slug validation error:", err);
      }
    };

    checkCaseMismatch();
  }, [slug]);

  const item = getItemBySlug(slug);
  const sections =
    detailsBySlug[slug] ?? detailsBySlug["audience-intelligence"] ?? [];

  /* ================= FAQ ================= */

  const faqSection = sections.find((section: DetailSection) => section.faq);

  /* ================= Highlight Letter ================= */

  const [clickedLetter, setClickedLetter] = useState<string | null>(null);

  useEffect(() => {
    const storedLetter = localStorage.getItem("clickedLetter");
    if (storedLetter) setClickedLetter(storedLetter);
  }, []);

  useEffect(() => {
    const storedLetter = localStorage.getItem("clickedLetter");
    if (storedLetter) {
      setTimeout(() => {
        const paragraphSection =
          document.getElementById("paragraph-section");
        paragraphSection?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        setTimeout(() => {
          localStorage.removeItem("clickedLetter");
        }, 1000);
      }, 100);
    }
  }, []);

  /* ================= Book Block Renderer ================= */

  const renderBlock = (block: ContentBlock, index: number) => {
    return (
        <div key={index} className="mb-10">
      
          {/* Heading */}
          {block.heading && (
            <h3 className="text-xl font-semibold text-black mb-4 tracking-tight">
              {block.heading}
            </h3>
          )}
      
          {/* Paragraph */}
          {block.type === "paragraph" && (
            <p className="text-lg  text-gray-700">
              {block.content}
            </p>
          )}
      
          {/* Content above the list */}
          {block.type === "list" && block.aboveList && (
            <p className="text-lg text-gray-700 mb-2">
              {block.aboveList}
            </p>
          )}
      
          {/* List */}
          {block.type === "list" && (
            <ul className="space-y-1">
              {block.items?.map((item: string, i: number) => (
                <li
                  key={i}
                  className="relative pl-6 text-lg text-gray-700 "
                >
                  {/* List bullet */}
                  <span className="absolute left-0 top-[12px] h-1.5 w-1.5 rounded-full bg-[#D53333]" />
      
                  {/* List content with bold support */}
                  <span
                    className="[&>strong]:font-semibold [&>strong]:text-black "
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                </li>
              ))}
            </ul>
          )}
      
          {/* Content below the list */}
          {block.type === "list" && block.belowList && (
            <p className="text-lg text-gray-700 mt-2">
              {block.belowList}
            </p>
          )}
          
        </div>
      );
  };

  return (
    <main className="overflow-hidden">

      {/* Alphabet Pattern Section */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-32 2xl:py-40 relative overflow-hidden mt-10 xl:mt-0">
        <AlphabetPattern highlightedLetter={clickedLetter} />
      </section>

      {/* ================= Paragraph Section ================= */}

      <section
        id="paragraph-section"
        className="relative custom-container "
      >
        

        <div className="container mx-auto text-center mb-12  space-y-6  border-b border-gray-200 py-12 section-bg-white-soft rounded-lg">
         
          <h1 className="text-4xl max-w-5xl mx-auto font-semibold text-[#d53333] mb-4 tracking-tight">
            {item?.customSubtitle}
              
            </h1>
            <h2 className="text-3xl font-semibold text-[#272d57]  tracking-tight">
              {item?.customTitle}
            </h2>
    
        </div>

        



        {/* ================= Book Layout ================= */}

        <div className="relative container mx-auto ">

          {/* Center Binding Line */}
          <div className="hidden md:block absolute left-1/2  h-full w-px bg-gray-300" />

          {sections
            .filter((section: DetailSection): section is DetailSection & { blocks: ContentBlock[] } => !!section.blocks)
            .map((section: DetailSection & { blocks: ContentBlock[] }, sectionIndex: number) => {

              const leftBlocks = section.blocks.filter(
                (block: ContentBlock) => block.page === "left"
              );

              const rightBlocks = section.blocks.filter(
                (block: ContentBlock) => block.page === "right"
              );

              return (
                <div
                  key={sectionIndex}
                  className="grid md:grid-cols-2 gap-x-20 relative text-[#1b1b1b] mb-32"
                >

                  {/* ===== LEFT PAGE ===== */}
                  <div className="relative">
                    <div className="absolute  top-0 h-full w-px bg-gray-200" />
                    <div className="absolute -bottom-14 left-0 text-sm text-gray-400 tracking-[0.3em] border border-gray-200 p-2 rounded">
                      {String(sectionIndex * 2 + 1).padStart(2, "0")}
                    </div>

                    <div className="pl-12">
                      {leftBlocks.map(renderBlock)}
                    </div>
                  </div>

                  {/* ===== RIGHT PAGE ===== */}
                  <div className="relative">
                    <div className="absolute right-0 top-0 h-full w-px bg-gray-200" />
                    <div className="absolute -bottom-14 right-0 text-sm text-gray-400 tracking-[0.3em] border border-gray-200 p-2 rounded">
                      {String(sectionIndex * 2 + 2).padStart(2, "0")}
                    </div>

                    <div className="pr-12">
                      {rightBlocks.map(renderBlock)}
                    </div>
                  </div>

                </div>
              );
            })}
        </div>
      </section>

      {/* ================= FAQ ================= */}

      {faqSection?.faq && (
        <FAQ
          faqData={faqSection.faq}
          title="FAQ"
          description="Get answers to common questions about audience intelligence and digital marketing strategies:"
        />
      )}
    </main>
  );
}